import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { HTTPCONSTANTS } from 'src/app/http/http-constants';
import { environment } from 'src/environments/environment';
import { site } from 'src/app/site-config';
import { SessionService } from 'src/app/services/session-service';
import { AccountV2Service } from 'src/app/services/account-v2-service';
import { PlatoPageFactsService } from 'src/app/services/plato-page-facts.service';

interface FollowupItem {
  kind: 'show_comps' | 'notify' | 'follow' | 'open' | 'open_url';
  label: string;
  registration?: string;
  url?: string;
  used?: boolean;
}
interface CompCard {
  registration: string;
  price: string;
  sold_date: string;
  /** Shape group heading ("Letter first" / "Number first"), set on the first card of each group. */
  groupStart?: string;
}
interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  html?: string;
  error?: boolean;
  /** Follow-up buttons offered under this answer (client-side actions). */
  followups?: { registration: string; comparables: CompCard[]; items: FollowupItem[] };
  /** Plate cards rendered under this message (e.g. comparable sales). */
  cards?: CompCard[];
  /** Live auction lots rendered under this message. */
  lots?: any[];
  lotsNote?: string;
}

/**
 * Action -> Plato moment. The server sends {"type":"action","action":"<tool>.<phase>"}
 * at each step; this table is the single place that matches chat actions to
 * animations (moments resolve to states via manifest.json, with fallbacks).
 * Keep in sync with the Plato playbook.
 */
export const PLATO_ACTIONS: { [action: string]: string } = {
  'value_plate.start': 'valuation',
  'search_plates.start': 'searching',
  'search_plates.slow': 'waiting',
  'plate_detail.start': 'thinking',
  'how_to.start': 'thinking',
  'followup.show_comps': 'delighted',
  // Notify = a plate NOT available now: alarm clock. Follow / track / favourite = a plate
  // you keep an eye on: garage (moment plateSaved).
  'followup.notify.done': 'notifySet',
  'followup.follow.done': 'plateSaved',
  'followup.notify.failed': 'sorry',
  'panel.reopen': 'happy',
  'panel.close': 'goodbye',
  'reply.unseen': 'getAttention',
  'auction.final_countdown': 'auctionClosing',
  'auction_lots.start': 'searching',
  'auction_for_plate.start': 'searching',
};

/**
 * PlateX assistant (dev2, admin-only beta). Answers come from the platex-assistant
 * service, which only words what PlateX's own tools return (valuation tool,
 * search, plate detail). Streams status lines + text over server-sent events.
 *
 * Visible only when: environment.assistantUrl is set AND (dev2 host or local dev)
 * AND the signed-in user is an admin (local dev may bypass with
 * environment.assistantDevBypass). The service re-checks admin on every request.
 */
async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

@Component({
  selector: 'pla-assistant-widget',
  templateUrl: './assistant-widget.component.html',
  styleUrls: ['./assistant-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;

  visible = false;
  open = false;
  /** Phones: the button slides out of the way while the page scrolls down, so
   *  it never sits over content being read, and returns on any scroll up. */
  tucked = false;
  private lastScrollY = 0;
  private lastScrollAt = 0;
  private lockedY = 0;
  busy = false;
  status = '';
  draft = '';
  messages: ChatMsg[] = [];
  // Plato, the assistant character (src/assets/assistant/plato, synced from
  // GitHub/platex-assistant-character). Each state is a self-contained animated SVG.
  readonly platoBase = 'assets/assistant/plato/';
  /** States present in manifest.json (refreshed on load); these are the v1.0.0 defaults. */
  platoStates: string[] = ['idle', 'listening', 'thinking', 'waiting', 'talking', 'happy', 'sorry', 'still'];
  private platoMeta: { [state: string]: any } = {};
  /** Optional designer wiring: manifest states may declare "moments": ["intro", ...]. */
  private manifestMoments: { [moment: string]: string[] } = {};
  /** Default moment -> preferred states, first available wins. Extend as Plato grows. */
  private readonly MOMENTS: { [moment: string]: string[] } = {
    intro: ['intro', 'philosopher', 'idle'],
    idle: ['idle', 'still'],
    listening: ['listening', 'idle'],
    thinking: ['thinking', 'idle'],
    deepThinking: ['philosopher', 'deep-thinking', 'thinking', 'idle'],
    waiting: ['waiting', 'thinking', 'idle'],
    valuation: ['valuation', 'thinking', 'idle'],
    searching: ['searching', 'thinking', 'idle'],
    talking: ['talking', 'idle'],
    happy: ['happy', 'idle'],
    sorry: ['sorry', 'idle'],
    notifySet: ['notify', 'happy', 'idle'],
    emailSent: ['email-sent', 'happy', 'idle'],
    plateSaved: ['garage', 'delighted', 'happy', 'idle'],
    getAttention: ['attention', 'happy', 'idle'],
    goodbye: ['goodbye', 'happy', 'idle'],
    auctionClosing: ['auction', 'surprised', 'idle'],
  };
  inputFocused = false;
  private streaming = false;
  private busySince = 0;
  private happyUntil = 0;
  private introUntil = 0;
  private introPlayed = false;
  private sorry = false;
  /** Expression the server picked for the last answer (from what the tools returned). */
  private reaction = '';
  /** The weigh-in plays to the end (hop on, needle swings and settles) even if the value comes back sooner. */
  private valuationUntil = 0;
  private readonly VALUATION_MIN_MS = 2800;
  private reactionUntil = 0;
  /** Occasional idle gag (hiccups, sneeze, backflip...) while the panel sits open and idle.
   *  Rules (character HANDOFF.md): first after ~45s of inactivity, then at most one every
   *  2-3 min, random without repeats, cut straight to listening on any type/click, never
   *  with prefers-reduced-motion or while the widget is collapsed. */
  private gag = '';
  private gagUntil = 0;
  private nextGagAt = 0;
  private gagBag: string[] = [];
  private lastGag = '';
  private listenUntil = 0;
  /** Long waits (slow name searches, or anything still busy after 10s): instead of the
   *  waiting loop, play idle gags back to back with a short breather, so people actually
   *  see them. Owner choice 2026-10-21 (gags were idle-only in the designer's HANDOFF). */
  private waitGag = '';
  private waitGagUntil = 0;
  private waitGagTimer?: any;
  private readonly WAIT_GAG_AFTER_MS = 10000;
  private readonly WAIT_GAG_GAP_MS = 2500;
  private lastActivity = Date.now();
  private gagTimer?: any;
  private readonly GAG_FIRST_MS = 45000;
  /** After goodbye (ends on an empty frame) Plato stays hidden until he is needed again. */
  platoGone = false;
  private goneTimer?: any;
  /** Attention ("hey, your answer's here") when a reply lands out of view; sparingly. */
  private attentionPending = false;
  private lastAttention = 0;
  private onVisibility = () => {
    if (!document.hidden && this.attentionPending) this.zone.run(() => this.attention());
  };
  /** DVLA lots already seen in their final countdown (each plays the auction moment once). */
  private countdownSeen: { [key: string]: boolean } = {};
  /** Current tool action from the server (e.g. value_plate.start); drives the busy animation. */
  private action = '';
  /** One-off animation triggered by a client action (e.g. Notify me set). */
  private oneShot = '';
  private oneShotUntil = 0;

  /** Halloween mode (character v2.0.0, manifest "halloween"; README "Halloween mode"). Only between
   *  halloween.from and halloween.until (UK time; outside it the block is ignored). One costume per session,
   *  picked at random when the manifest loads. Costume files replace the normal state of the same name (same timing
   *  and loop rules); intro, goodbye and idle gags come from the costume only ('hw:' pseudo-states); anything
   *  the costume doesn't have plays the normal state behind a puff (see ngAfterViewChecked). */
  private hw: any = null;
  private costume = '';
  private get hwCostume(): any {
    const h = this.hw;
    if (!h || !h.costumes || !this.costume) return null;
    const now = Date.now();
    return now >= Date.parse(h.from) && now < Date.parse(h.until) ? h.costumes[this.costume] || null : null;
  }
  private hwActive(): boolean {
    const h = this.hw;
    const now = Date.now();
    return !!h && !!h.costumes && now >= Date.parse(h.from) && now < Date.parse(h.until);
  }
  private chooseCostume(): void {
    if (!this.hwActive() || this.costume) return;
    const names = Object.keys(this.hw.costumes);
    let c = '';
    try {
      c = sessionStorage.getItem('plato-costume') || '';
    } catch {}
    if (!names.includes(c)) {
      c = names[Math.floor(Math.random() * names.length)];
      try {
        sessionStorage.setItem('plato-costume', c);
      } catch {}
    }
    this.costume = c;
    this.prefetchCostume();
  }
  /** Warm the cache so costume states don't flash the previous pose while they load. */
  private prefetchCostume(): void {
    const c = this.hwCostume;
    if (!c) return;
    const files = [...Object.values(c.states), c.intro?.file, c.goodbye?.file, ...(c.idleGags || []).map((g: any) => g.file),
      this.hw.fallback?.file].filter(Boolean) as string[];
    files.forEach((f) => this.loadSvg(this.platoBase + this.baseName(f)).catch(() => {}));
  }
  private baseName(f: string): string {
    return f.split('/').pop() || f;
  }
  /** Halloween greeting, shown as Plato's first message once the costume intro has played. */
  get halloweenGreeting(): string {
    const c = this.hwCostume;
    return c && Date.now() >= this.introUntil ? c.greeting || '' : '';
  }
  /** While a costume intro plays, the welcome text waits so the greeting can follow it. */
  get costumeIntroRunning(): boolean {
    return !!this.hwCostume && Date.now() < this.introUntil;
  }
  private idleGagStates(): string[] {
    const c = this.hwCostume;
    if (c) return (c.idleGags || []).map((_: any, i: number) => 'hw:gag:' + i); // normal gags pause meanwhile
    return (this.manifestMoments['idleGag'] || []).filter((g) => this.platoStates.includes(g));
  }
  private goodbyeState(): string {
    return this.hwCostume?.goodbye ? 'hw:goodbye' : this.pick('goodbye');
  }
  /** The file to draw for a state: the costume's version when it has one, otherwise the normal one. */
  private fileFor(state: string): string {
    const c = this.hwCostume;
    if (c) {
      if (this.reducedMotion() && c.states.still) return this.platoBase + this.baseName(c.states.still);
      let f = '';
      if (state === 'hw:intro') f = c.intro?.file;
      else if (state === 'hw:goodbye') f = c.goodbye?.file;
      else if (state.startsWith('hw:gag:')) f = (c.idleGags || [])[+state.slice(7)]?.file;
      else f = c.states[state];
      if (f) return this.platoBase + this.baseName(f);
    }
    return this.platoBase + (state.startsWith('hw:') ? 'idle' : state) + '.svg';
  }
  private isCostumeFile(src: string): boolean {
    return /\/halloween-/.test(src) && !/halloween-puff/.test(src);
  }

  private pick(moment: string): string {
    // A moment can also be a state's own name (the server sends tricks like 'backflip' directly).
    const prefs = [...(this.manifestMoments[moment] || []), ...(this.MOMENTS[moment] || []), moment, 'idle'];
    return prefs.find((st) => this.platoStates.includes(st)) || 'idle';
  }
  private durationOf(state: string, fallback: number): number {
    if (state.startsWith('hw:')) {
      const c = this.hwCostume || {};
      const it = state === 'hw:intro' ? c.intro : state === 'hw:goodbye' ? c.goodbye : (c.idleGags || [])[+state.slice(7)];
      return (it && it.duration_ms) || fallback;
    }
    const d = this.platoMeta[state] && this.platoMeta[state].duration_ms;
    return typeof d === 'number' && d > 0 ? d : fallback;
  }
  private refreshIn(ms: number): void {
    setTimeout(() => this.cdr.markForCheck(), ms + 30);
  }

  /** Which Plato animation fits what the chat is doing right now. */
  get mood(): string {
    const now = Date.now();
    if (now < this.valuationUntil) return this.pick('valuation');
    if (this.oneShot && now < this.oneShotUntil) return this.oneShot;
    if (this.busy) {
      if (this.streaming) return this.pick('talking');
      if (this.waitGag && now < this.waitGagUntil) return this.waitGag;
      if (this.action && PLATO_ACTIONS[this.action]) return this.pick(PLATO_ACTIONS[this.action]);
      return now - this.busySince > 3000 ? this.pick('deepThinking') : this.pick('thinking');
    }
    if (now < this.introUntil) return this.hwCostume?.intro ? 'hw:intro' : this.pick('intro');
    if (this.sorry) return this.pick('sorry');
    if (this.reaction && now < this.reactionUntil) return this.pick(this.reaction);
    if (now < this.happyUntil) return this.pick('happy');
    if (this.gag && now < this.gagUntil) return this.gag;
    if (now < this.listenUntil || (this.inputFocused && this.draft.trim())) return this.pick('listening');
    return this.pick('idle');
  }
  /** True while the weigh-in is still playing (it outlasts fast answers). */
  get weighing(): boolean {
    return Date.now() < this.valuationUntil;
  }
  /** The status row (with Plato) stays up until the weigh-in has finished, so it isn't restarted. */
  get showStatusRow(): boolean {
    return (this.busy && !!this.status) || this.weighing;
  }
  get statusText(): string {
    return this.status || 'Weighing it up…';
  }

  /** Shown larger while weighing so the scale and needle can actually be seen. */
  get bigMood(): boolean {
    return this.mood === 'valuation';
  }

  // Safari (WebKit) shares ONE animation timeline per SVG image URL across every
  // <img> that uses it, and the hidden preloads start those timelines when the
  // panel opens. A one-off (intro, notify, camera...) shown later would already
  // be at its last frame. A fresh URL each time Plato changes mood gives him a
  // fresh timeline, so every animation starts from frame one in every browser.
  private lastMood = '';
  private moodToken = 0;
  /** The conversation's Plato is drawn inline, not as an <img>: Safari leaves smeared ghost copies of an animated
   *  SVG shown through <img> (its repaints miss where the moving parts were, worst on the weighing scale). Inline
   *  SVG repaints cleanly. The files are our own assets; their classes, keyframes and ids are already prefixed per
   *  state (pxc-<state>-), and the one bare `*` rule (reduced motion) is scoped to the stage before inserting. */
  @ViewChild('stageSvg') stageSvg?: ElementRef<HTMLElement>;
  private svgCache: Record<string, string> = {};
  private shownSrc = '';
  ngAfterViewChecked(): void {
    const host = this.stageSvg?.nativeElement;
    if (!host) {
      this.shownSrc = '';
      return;
    }
    const src = this.moodSrc;
    if (src === this.shownSrc) return;
    const prev = this.shownSrc;
    this.shownSrc = src;
    const file = src.split('?')[0];
    const put = (svg: string) => {
      if (this.shownSrc === src && this.stageSvg?.nativeElement === host) host.innerHTML = svg; // re-inserting restarts it
    };
    // Halloween: a state the costume doesn't have pops off the costume (and back) behind a puff, swapping at 300 ms.
    const puff = !!this.hwCostume && !!prev && this.isCostumeFile(prev) !== this.isCostumeFile(file) && !this.reducedMotion();
    if (puff) this.playPuff(host.parentElement);
    const show = () => this.loadSvg(file).then(put).catch(() => {});
    if (puff) setTimeout(show, 300);
    else show();
  }
  private loadSvg(file: string): Promise<string> {
    if (this.svgCache[file]) return Promise.resolve(this.svgCache[file]);
    return fetch(file)
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => {
        if (!t.includes('<svg')) throw new Error('not svg');
        t = t.replace(/\*\s*\{/g, '.aw-stage *{');
        return (this.svgCache[file] = t);
      });
  }
  private playPuff(stage: HTMLElement | null): void {
    const fb = this.hw?.fallback;
    if (!stage || !fb?.file) return;
    const img = document.createElement('img');
    img.className = 'aw-puff';
    img.alt = '';
    img.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:1';
    img.src = this.platoBase + this.baseName(fb.file) + '?p=' + Date.now(); // fresh timeline each time
    stage.appendChild(img);
    setTimeout(() => img.remove(), fb.duration_ms || 600);
  }

  get moodSrc(): string {
    const mood = this.mood;
    if (mood !== this.lastMood) {
      this.lastMood = mood;
      this.moodToken = (this.moodToken + 1) % 1e6;
    }
    return this.fileFor(mood) + '?m=' + this.moodToken;
  }

  /** Load the character manifest so new states/moments work without code changes. */
  private loadManifest(): void {
    fetch(this.platoBase + 'manifest.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (!m || !m.states) return;
        this.platoMeta = m.states;
        this.hw = m.halloween || null;
        this.chooseCostume(); // the launcher shows the costume too, so pick (or restore) it straight away
        this.platoStates = Object.keys(m.states);
        this.manifestMoments = {};
        Object.keys(m.states).forEach((st) =>
          ((m.states[st] && m.states[st].moments) || []).forEach((mo: string) =>
            (this.manifestMoments[mo] = this.manifestMoments[mo] || []).push(st)
          )
        );
        this.cdr.markForCheck();
      })
      .catch(() => {});
  }

  // Public (edge) mode is the restricted apprentice: no valuations or auctions yet.
  readonly apprentice = site.assistant === 'edge';
  readonly suggestions = this.apprentice
    ? ['Find a plate for my name', "What's a dateless plate?", "What's the most famous plate ever?"]
    : ["What's D88 worth?", 'Good plates for my name Hugh', "What's ending soon at auction?"];
  readonly inputHint = this.apprentice ? 'Ask me about number plates' : "e.g. What's 11 HXS worth?";

  private sub?: Subscription;
  private abort?: AbortController;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.lastScrollAt = Date.now();
    const y = window.scrollY || 0;
    if (this.bubble && Math.abs(y - this.bubbleScrollY) > 400) this.hideBubble(); // reading on: get out of the way
    const dy = y - this.lastScrollY;
    if (Math.abs(dy) < 8) return;
    this.lastScrollY = y;
    const tuck = window.innerWidth <= 900 && dy > 0 && y > 120;
    if (tuck !== this.tucked) {
      this.tucked = tuck;
      this.cdr.markForCheck();
    }
  }

  constructor(
    private session: SessionService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private http: HttpClient,
    private router: Router,
    private account: AccountV2Service,
    private pageFacts: PlatoPageFactsService,
    private host: ElementRef<HTMLElement>
  ) {}

  /** Phones: the panel fills the VISIBLE area (above the keyboard) and the page behind it can't scroll.
   *  Without this, iOS shifts the full-screen panel when the keyboard opens and taps miss the box. */
  private onViewport = () => {
    const vv = (window as any).visualViewport;
    const el = this.host.nativeElement;
    if (!vv) return;
    el.style.setProperty('--aw-vh', vv.height + 'px');
    el.style.setProperty('--aw-top', vv.offsetTop + 'px');
  };
  private get isPhone(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 520;
  }
  private fitToViewport(on: boolean): void {
    if (typeof window === 'undefined') return;
    const vv = (window as any).visualViewport;
    const html = document.documentElement;
    const body = document.body.style;
    if (on && this.isPhone) {
      this.onViewport();
      vv?.addEventListener('resize', this.onViewport);
      vv?.addEventListener('scroll', this.onViewport);
      if (!html.classList.contains('aw-lock')) {
        // iOS ignores overflow:hidden on html/body, so pin the body in place instead. A page left scrolled
        // under the full-screen panel made iOS hit-test taps at the wrong place: the box, chips and close
        // button stopped responding. Pinning also keeps the reader's place for when the panel closes.
        this.lockedY = window.scrollY || 0;
        body.position = 'fixed';
        body.top = -this.lockedY + 'px';
        body.left = '0';
        body.right = '0';
        body.width = '100%';
        html.classList.add('aw-lock');
      }
    } else {
      vv?.removeEventListener('resize', this.onViewport);
      vv?.removeEventListener('scroll', this.onViewport);
      if (html.classList.contains('aw-lock')) {
        html.classList.remove('aw-lock');
        body.position = body.top = body.left = body.right = body.width = '';
        window.scrollTo({ top: this.lockedY, behavior: 'instant' as ScrollBehavior });
      }
    }
  }

  /** iPhone Safari sometimes reports a touch on the panel (touchstart/touchend on the box or a button) but never
   *  turns it into a click or focus, so nothing in the full-screen panel responded (seen on the owner's iPhone). For a quick, still tap inside the panel we do it ourselves: focus the box (still
   *  inside the touch, so the keyboard opens) or press the button, and cancel Safari's own click so nothing
   *  fires twice. Drags (scrolling the conversation) and taps in a box already being typed in are left alone. */
  private tapStart?: { x: number; y: number; t: number };
  private onTouchStart = (e: TouchEvent): void => {
    const t = e.touches[0];
    this.tapStart = e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY, t: Date.now() } : undefined;
  };
  private onTouchEnd = (e: TouchEvent): void => {
    const s = this.tapStart;
    this.tapStart = undefined;
    const t = e.changedTouches[0];
    const target = e.target as HTMLElement | null;
    if (!s || !t || !this.open || !target?.closest?.('.aw-panel')) return;
    if (Date.now() - s.t > 700 || Math.hypot(t.clientX - s.x, t.clientY - s.y) > 10) return;
    const field = target.closest('textarea, input') as HTMLElement | null;
    if (field) {
      if (document.activeElement === field) return; // already typing: let Safari place the caret
      e.preventDefault();
      field.focus();
      return;
    }
    const btn = target.closest('button, a[href]') as HTMLElement | null;
    if (!btn || (btn as HTMLButtonElement).disabled) return;
    e.preventDefault();
    this.zone.run(() => btn.click());
  };

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        const host = this.host.nativeElement;
        host.addEventListener('touchstart', this.onTouchStart, { passive: true });
        host.addEventListener('touchend', this.onTouchEnd, { passive: false });
      });
    }
    this.loadManifest();
    this.zone.runOutsideAngular(() => this.scheduleGags());
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
    this.refreshVisibility();
    const ev = this.session.sessionStateChange;
    if (ev && ev.subscribe) this.sub = ev.subscribe(() => this.refreshVisibility());
    this.routeSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) this.queueBubble(); // asks again on a later page (rate-limited)
    });
  }

  private reducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  /** Random without repeats: deal from a shuffled bag, refill when empty. */
  private nextGag(gags: string[]): string {
    this.gagBag = this.gagBag.filter((g) => gags.includes(g)); // costume on/off changes the set
    if (!this.gagBag.length) {
      this.gagBag = [...gags].sort(() => Math.random() - 0.5);
      if (this.gagBag.length > 1 && this.gagBag[0] === this.lastGag) this.gagBag.push(this.gagBag.shift()!);
    }
    return (this.lastGag = this.gagBag.shift()!);
  }
  private scheduleGags(): void {
    if (this.gagTimer) return;
    this.gagTimer = setInterval(() => {
      const now = Date.now();
      const gags = this.idleGagStates();
      if (!this.open || !gags.length || this.reducedMotion()) return;
      if (this.gag && now < this.gagUntil) return;
      if (now - this.lastActivity < this.GAG_FIRST_MS || now < this.nextGagAt) return;
      if (this.mood !== this.pick('idle')) return; // only from Plato's core idle state
      this.zone.run(() => {
        this.gag = this.nextGag(gags);
        const d = this.durationOf(this.gag, 6000);
        this.gagUntil = now + d;
        this.nextGagAt = now + d + 120000 + Math.random() * 60000;
        this.cdr.markForCheck();
        this.refreshIn(d);
      });
    }, 5000);
  }

  /** Long wait: one gag, a breather on the waiting loop, then another (no repeats). */
  private startWaitGags(): void {
    if (this.waitGagTimer) clearTimeout(this.waitGagTimer);
    this.waitGagTimer = undefined;
    const gags = this.idleGagStates();
    if (!this.busy || this.streaming || !gags.length || this.reducedMotion()) return;
    const wait = this.valuationUntil - Date.now();
    if (wait > 0) {
      // let the weigh-in finish first
      this.waitGagTimer = setTimeout(() => this.zone.run(() => this.startWaitGags()), wait + 200);
      return;
    }
    this.waitGag = this.nextGag(gags);
    const d = this.durationOf(this.waitGag, 6000);
    this.waitGagUntil = Date.now() + d;
    this.cdr.markForCheck();
    this.refreshIn(d);
    this.waitGagTimer = setTimeout(() => this.zone.run(() => this.startWaitGags()), d + this.WAIT_GAG_GAP_MS);
  }
  private stopWaitGags(): void {
    if (this.waitGagTimer) clearTimeout(this.waitGagTimer);
    this.waitGagTimer = undefined;
    this.waitGag = '';
    this.waitGagUntil = 0;
  }

  /** Any type or click: note it, and cut an idle gag straight to listening. */
  noteActivity(): void {
    const now = Date.now();
    this.lastActivity = now;
    if (this.gag && now < this.gagUntil) {
      this.gag = '';
      this.gagUntil = 0;
      this.listenUntil = now + 1500;
      this.refreshIn(1500);
      this.cdr.markForCheck();
    }
  }

  /** A reply landed while the panel was closed or the tab hidden: knock on the glass. */
  private attention(): void {
    this.attentionPending = false;
    if (Date.now() - this.lastAttention < 60000) return;
    this.lastAttention = Date.now();
    this.showPlato();
    this.play('reply.unseen');
    this.cdr.markForCheck();
  }
  private showPlato(): void {
    this.platoGone = false;
    if (this.goneTimer) clearTimeout(this.goneTimer);
    this.goneTimer = undefined;
    this.cdr.markForCheck();
  }

  /** "Looking for a plate?" bubble beside the launcher. Shows a few seconds after the first page of a visit
   *  and again on later pages, at most every bubbleGapMs and bubbleMax times per visit (session). It stays up for
   *  bubbleMs (the CSS animation fades it out) unless Plato is opened or the page is scrolled well past it. */
  bubble = false;
  readonly bubbleMs = 12000;
  private readonly bubbleGapMs = 180000;
  private readonly bubbleMax = 3;
  private bubbleTimer?: any;
  private bubbleScrollY = 0;
  private routeSub?: Subscription;
  /** Halloween: the night colour of the costume's own tile (intro/gag/goodbye files), used behind the
   *  conversation so the tile blends in. Empty outside Halloween. */
  get nightColour(): string {
    const night: Record<string, string> = { monster: '#1F2433', vampire: '#221A2E', werewolf: '#18243A' };
    return (this.hwCostume && night[this.costume]) || '';
  }
  /** In character while Plato's in a Halloween costume. */
  get bubbleText(): string {
    const lines: Record<string, string> = {
      vampire: 'Good evening… looking for a plate?',
      werewolf: 'Shall I sniff out a plate for you?',
      monster: 'Fully charged! Looking for a plate?',
    };
    return (this.hwCostume && lines[this.costume]) || 'Looking for a plate? Ask me.';
  }
  private queueBubble(): void {
    if (!this.visible || this.open || this.bubble || this.bubbleTimer) return;
    let seen = { n: 0, last: 0 };
    try {
      seen = { ...seen, ...JSON.parse(sessionStorage.getItem('plato-bubble') || '{}') };
    } catch {}
    if (seen.n >= this.bubbleMax || Date.now() - seen.last < this.bubbleGapMs) return;
    this.bubbleTimer = setTimeout(() => {
      this.bubbleTimer = undefined;
      if (this.open || !this.visible) return;
      try {
        sessionStorage.setItem('plato-bubble', JSON.stringify({ n: seen.n + 1, last: Date.now() }));
      } catch {}
      this.bubble = true;
      this.bubbleScrollY = window.scrollY || 0;
      this.cdr.markForCheck();
      this.bubbleTimer = setTimeout(() => this.hideBubble(), this.bubbleMs);
    }, 2500);
  }
  private hideBubble(): void {
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
    this.bubbleTimer = undefined;
    if (!this.bubble) return;
    this.bubble = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('touchstart', this.onTouchStart);
    this.host.nativeElement.removeEventListener('touchend', this.onTouchEnd);
    if (this.gagTimer) clearInterval(this.gagTimer);
    if (this.tick) clearInterval(this.tick);
    if (this.goneTimer) clearTimeout(this.goneTimer);
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
    if (this.routeSub) this.routeSub.unsubscribe();
    this.stopWaitGags();
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.sub) this.sub.unsubscribe();
    if (this.abort) this.abort.abort();
    this.fitToViewport(false);
  }

  private refreshVisibility(): void {
    const env: any = environment;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const hostOk = host.includes('dev2') || host === 'localhost' || host === '127.0.0.1';
    const userOk = this.session.isAdmin() || (!env.production && !!env.assistantDevBypass);
    this.visible =
      !!env.assistantUrl && (site.assistant === 'edge' || (site.assistant === 'admin' && hostOk && userOk));
    if (!this.visible) this.open = false;
    this.queueBubble();
    this.cdr.markForCheck();
  }

  toggle(): void {
    // Phones: the round button slides back in under the thumb as the page scrolls up, so a tap that only
    // meant to stop the scroll opened the full-screen panel. Ignore taps while the page is still moving.
    if (!this.open && this.isPhone && Date.now() - this.lastScrollAt < 400) return;
    this.open = !this.open;
    this.hideBubble();
    this.gag = '';
    this.gagUntil = 0;
    this.lastActivity = Date.now();
    if (this.open) this.showPlato();
    if (!this.open) {
      // Closed: Plato waves goodbye in the pill. The animation ends on an empty frame,
      // so he stays hidden afterwards (the pill remains) until reopened or a reply lands.
      this.valuationUntil = 0;
      this.play('panel.close');
      const left = this.oneShotUntil - Date.now();
      if (this.goneTimer) clearTimeout(this.goneTimer);
      this.goneTimer = setTimeout(() => {
        this.zone.run(() => {
          this.goneTimer = undefined;
          if (!this.open && this.oneShot === this.goodbyeState()) {
            // There's no pill to hold his place any more, so he fades back in after a short break.
            this.platoGone = true;
            this.cdr.markForCheck();
            this.goneTimer = setTimeout(() => this.zone.run(() => this.showPlato()), 1500);
          }
        });
      }, Math.max(0, left - 60));
    } else if (this.open && !this.introPlayed) {
      // First open: Plato's intro (his philosopher side) plays once, then idle.
      this.introPlayed = true;
      const st = this.hwCostume?.intro ? 'hw:intro' : this.pick('intro');
      if (st !== 'idle') {
        this.introUntil = Date.now() + this.durationOf(st, 2500);
        this.refreshIn(this.introUntil - Date.now());
      }
    } else if (this.open && !this.busy) {
      // Reopened later in the visit: back with a quick happy beat.
      this.play('panel.reopen');
    }
    this.cdr.markForCheck();
    this.fitToViewport(this.open);
    const touch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (this.open && !touch) setTimeout(() => this.input?.nativeElement.focus(), 50);
  }

  ask(text?: string): void {
    const q = (text ?? this.draft).trim();
    if (!q || this.busy) return;
    this.draft = '';
    this.messages.push({ role: 'user', content: q, html: this.render(q, true) });
    const reply: ChatMsg = { role: 'assistant', content: '', html: '' };
    this.messages.push(reply);
    this.busy = true;
    this.sorry = false;
    this.reaction = '';
    this.valuationUntil = 0;
    this.gagUntil = 0;
    this.lastActivity = Date.now();
    this.streaming = false;
    this.action = '';
    this.oneShotUntil = 0;
    this.introUntil = 0;
    this.busySince = Date.now();
    this.refreshIn(3000);
    this.stopWaitGags();
    this.waitGagTimer = setTimeout(() => this.zone.run(() => this.startWaitGags()), this.WAIT_GAG_AFTER_MS);
    this.status = 'Thinking…';
    this.cdr.markForCheck();
    this.scrollSoon();
    this.stream(reply).finally(() => {
      this.zone.run(() => {
        this.busy = false;
        this.stopWaitGags();
        this.streaming = false;
        this.status = '';
        if (!reply.error && reply.content) {
          // Brief "happy" beat when an answer lands, then back to idle.
          const after = Math.max(0, this.valuationUntil - Date.now());
          if (this.reaction) {
            // Show the reaction long enough to read: loops ~2.6s, one-shots their length.
            const st = this.pick(this.reaction);
            const meta = this.platoMeta[st] || {};
            const hold = meta.loop === false ? this.durationOf(st, 2600) : Math.max(2600, this.durationOf(st, 2600));
            this.reactionUntil = Date.now() + after + hold;
            this.refreshIn(after + hold);
          } else {
            const hold = this.durationOf(this.pick('happy'), 1200) + 100;
            this.happyUntil = Date.now() + hold;
            this.refreshIn(hold);
          }
          this.lastActivity = Date.now();
          // Answer arrived out of view (panel closed or tab hidden): attention, sparingly.
          if (typeof document !== 'undefined' && document.hidden) this.attentionPending = true;
          else if (!this.open) this.attention();
        }
        if (!reply.content && !reply.error) {
          reply.error = true;
          this.sorry = true;
          reply.html = this.render('Sorry, no answer came back. Please try again.');
        }
        this.cdr.markForCheck();
        this.scrollSoon();
      });
    });
  }

  onKey(e: KeyboardEvent): void {
    this.noteActivity();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.ask();
    }
  }

  /** Which page the user is on, so "this plate" / "these results" mean something.
   *  Only structured fields from the URL are sent (the server re-sanitises them). */
  private pageContext(): {
    kind: string; registration?: string; term?: string; series?: string; listing?: any; sections?: string[];
  } {
    const url = (this.router.url || '').split('?')[0].split('#')[0];
    const seg = url.split('/').filter(Boolean).map((x) => decodeURIComponent(x));
    const first = (seg[0] || '').toLowerCase();
    if ((first === 'plate' || first === 'plate-detail') && seg[1]) {
      // The dealer listings the page shows (if loaded), so Plato quotes the same market.
      const listing = this.pageFacts.getListing(seg[1]);
      // The plate page's sections that are actually showing, so Plato only points to what's there.
      const sections = ['sec-overview', 'sec-compare', 'sec-market', 'sec-history', 'sec-similar'].filter(
        (id) => !!document.getElementById(id)
      );
      return listing === undefined
        ? { kind: 'plate', registration: seg[1], sections }
        : { kind: 'plate', registration: seg[1], listing: listing || { sellers: 0 }, sections };
    }
    if ((first === 'search-results' || first === 'search-prod-style') && seg[1]) return { kind: 'search', term: seg[1] };
    if (first === 'auctions' || first === 'live-auctions') return { kind: 'auctions' };
    if (first === 'new-release') return seg[1] ? { kind: 'new_release', series: seg[1] } : { kind: 'new_release' };
    if (first === 'account') return { kind: 'account' };
    if (!first) return { kind: 'home' };
    return { kind: 'other' };
  }

  /** Dateless comps split into "Letter first" (D 37) and "Number first" (19 D), the plate's own shape
   *  first, keeping the valuation tool's order inside each group. Other styles are left as one list. */
  private groupByShape(reg: string, comps: CompCard[]): CompCard[] {
    const dateless = (r: string) => /^([A-Z]{1,3}\d{1,4}|\d{1,4}[A-Z]{1,3})$/.test((r || '').toUpperCase().replace(/[^A-Z0-9]/g, ''));
    const letterFirst = (r: string) => /^[A-Z]/.test((r || '').toUpperCase().replace(/[^A-Z0-9]/g, ''));
    if (!dateless(reg) || !comps.every((c) => dateless(c.registration))) return comps.map((c) => ({ ...c }));
    const own = letterFirst(reg);
    const groups: [string, CompCard[]][] = [
      [own ? 'Letter first' : 'Number first', comps.filter((c) => letterFirst(c.registration) === own)],
      [own ? 'Number first' : 'Letter first', comps.filter((c) => letterFirst(c.registration) !== own)],
    ];
    const out: CompCard[] = [];
    groups.forEach(([label, list]) => list.forEach((c, i) => out.push({ ...c, groupStart: i === 0 ? label : undefined })));
    return out;
  }

  /** Standard UK spacing for a registration: D 88, 19 D, AB12 CDE, A123 BCD, ABC 123D, AIZ 1234. */
  spaced(reg: string): string {
    const r = (reg || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    let m = /^([A-Z]{2}\d{2})([A-Z]{3})$/.exec(r); // current
    if (m) return m[1] + ' ' + m[2];
    m = /^([A-Z]\d{1,3})([A-Z]{3})$/.exec(r); // prefix
    if (m) return m[1] + ' ' + m[2];
    m = /^([A-Z]{3})(\d{1,3}[A-Z])$/.exec(r); // suffix
    if (m) return m[1] + ' ' + m[2];
    m = /^([A-Z]+)(\d+)$/.exec(r) || /^(\d+)([A-Z]+)$/.exec(r); // dateless / NI
    if (m) return m[1] + ' ' + m[2];
    return reg;
  }

  /** Refresh the PlateX session token (true if refreshed). */
  private async refreshSession(): Promise<boolean> {
    if (!this.session.isLoggedIn()) return false;
    try {
      await firstValueFrom(this.session.RefreshToken());
      return true;
    } catch {
      return false;
    }
  }

  private async stream(reply: ChatMsg): Promise<void> {
    const env: any = environment;
    // Send plain prior turns only; drop failed replies AND the question that
    // failed, so it isn't silently re-asked. Tools stay server-side.
    const history: { role: string; content: string }[] = [];
    this.messages.forEach((m, i) => {
      if (m === reply || m.error || !m.content) return;
      const next = this.messages[i + 1];
      if (m.role === 'user' && next && next !== reply && next.error) return;
      history.push({ role: m.role, content: m.content });
    });
    history.splice(0, Math.max(0, history.length - 12));
    this.abort = new AbortController();
    let timedOut = false;
    const watchdog = setTimeout(() => {
      timedOut = true;
      this.abort?.abort();
    }, 100000);
    try {
      // costume: so Plato knows what he's dressed as (the server only accepts its own costume names)
      const body = JSON.stringify({ messages: history, page: { ...this.pageContext(), ...(this.hwCostume ? { costume: this.costume } : {}) } });
      // 'edge' (staging): no PlateX session is sent. CloudFront signs the request to the assistant's
      // IAM-only function URL, which needs the SHA-256 of a POST body from the client.
      const headers: { [k: string]: string } =
        site.assistant === 'edge'
          ? { 'Content-Type': 'application/json', 'x-amz-content-sha256': await sha256Hex(body) }
          : { 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.session.getToken() };
      const send = () =>
        fetch(env.assistantUrl.replace(/\/$/, '') + '/chat', { method: 'POST', headers, body, signal: this.abort!.signal });
      let res = await send();
      // fetch() bypasses the token interceptor, so an expired access token isn't
      // refreshed automatically: refresh once and retry, as the interceptor would.
      if (res.status === 401 && site.assistant !== 'edge' && (await this.refreshSession())) res = await send();
      if (!res.ok || !res.body) {
        let msg = 'Sorry, the assistant is unavailable right now.';
        if (res.status === 401) {
          // Distinguish "not an admin" from "signed out": when the site's background
          // token renewal fails, the session is marked expired while the page stays
          // on screen, so the user still looks signed in.
          if (!this.session.isLoggedIn()) msg = 'You\'ve been signed out of PlateX. Please sign in again to use Plato.';
          else if (this.session.isAdmin()) msg = 'Your sign-in has expired. Please sign out and back in, then ask again.';
          else msg = 'The assistant is only available to PlateX admins while in testing.';
        }
        if (res.status === 429) msg = 'Too many questions in a short time. Please try again in a few minutes.';
        this.fail(reply, msg);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let i: number;
        while ((i = buf.indexOf('\n\n')) >= 0) {
          const line = buf.slice(0, i).trim();
          buf = buf.slice(i + 2);
          if (line.startsWith('data:')) this.onEvent(reply, JSON.parse(line.slice(5)));
        }
      }
    } catch (e: any) {
      if (e && e.name === 'AbortError' && !timedOut) return;
      this.fail(reply, timedOut ? 'Sorry, that took too long. Please ask again.' : 'Sorry, the assistant could not be reached.');
    } finally {
      clearTimeout(watchdog);
    }
  }

  private onEvent(reply: ChatMsg, ev: any): void {
    this.zone.run(() => {
      if (ev.type === 'status') this.status = ev.text;
      else if (ev.type === 'action' && typeof ev.action === 'string') {
        this.action = /\.done$/.test(ev.action) ? '' : ev.action;
        if (ev.action === 'search_plates.slow' && !this.waitGag) this.startWaitGags();
        if (ev.action === 'value_plate.start' && Date.now() >= this.valuationUntil) {
          this.valuationUntil = Date.now() + this.VALUATION_MIN_MS;
          this.refreshIn(this.VALUATION_MIN_MS);
        }
      } else if (ev.type === 'cards' && ev.kind === 'sales' && Array.isArray(ev.items)) {
        reply.cards = ev.items;
      } else if (ev.type === 'cards' && ev.kind === 'auction' && Array.isArray(ev.items)) {
        reply.lots = ev.items;
        reply.lotsNote = ev.fees_note || '';
        this.startTick();
      } else if (ev.type === 'followups' && Array.isArray(ev.items)) {
        reply.followups = { registration: ev.registration, comparables: ev.comparables || [], items: ev.items };
      }
      // goodbye now belongs to closing the chat (it ends on an empty frame); a spoken
      // "bye" in the chat gets a wink instead.
      else if (ev.type === 'reaction' && typeof ev.mood === 'string') this.reaction = ev.mood === 'goodbye' ? 'wink' : ev.mood;
      else if (ev.type === 'text') {
        this.status = '';
        this.streaming = true;
        this.stopWaitGags();
        reply.content += ev.delta;
        reply.html = this.render(reply.content);
      } else if (ev.type === 'error') {
        this.fail(reply, ev.text);
      }
      this.cdr.markForCheck();
      this.scrollSoon();
    });
  }

  /** Play a one-off Plato moment (e.g. after a client-side action). */
  private play(action: string): void {
    const moment = PLATO_ACTIONS[action] || 'happy';
    const st = moment === 'goodbye' ? this.goodbyeState() : this.pick(moment);
    this.oneShot = st;
    const meta = st.startsWith('hw:') ? { loop: false } : this.platoMeta[st] || {};
    const d = meta.loop === false ? this.durationOf(st, 2600) : Math.max(2200, this.durationOf(st, 2200));
    this.oneShotUntil = Date.now() + d;
    this.lastActivity = Date.now();
    this.refreshIn(d);
  }

  private say(html: string, extra: Partial<ChatMsg> = {}): void {
    this.messages.push({ role: 'assistant', content: '', html, ...extra });
    this.cdr.markForCheck();
    this.scrollSoon();
  }

  runFollowup(m: ChatMsg, item: FollowupItem): void {
    if (item.used || this.busy || !m.followups) return;
    const reg = m.followups.registration;
    if (item.kind === 'open') {
      this.openPlate(reg);
      return;
    }
    if (item.kind === 'open_url' && item.url) {
      this.openSite(item.url);
      return;
    }
    item.used = true;
    if (item.kind === 'show_comps') {
      const comps = m.followups.comparables || [];
      if (!comps.length) {
        this.say(this.render('There are no individual comparable sales to show for ' + reg + '.'));
        return;
      }
      this.say(this.render('The auction sales behind the **' + this.spaced(reg) + '** estimate:'), {
        cards: this.groupByShape(reg, comps),
      });
      this.play('followup.show_comps');
      return;
    }
    if (item.kind === 'follow') {
      // For sale now: follow / track it (Account -> Followed plates).
      if (!this.session.isLoggedIn()) {
        item.used = false;
        this.say(this.render('Sign in to PlateX first and I can follow ' + reg + ' for you.'), { error: true });
        this.play('followup.notify.failed');
        return;
      }
      this.account.follow(reg, 'tracking').subscribe(
        () => {
          this.say(this.render('Saved. **' + reg + '** is now in your **Followed plates**, so you can keep track of it there.'));
          this.play('followup.follow.done');
        },
        () => {
          item.used = false;
          this.say(this.render('Sorry, I couldn\'t follow ' + reg + ' just now. Please try again.'), { error: true });
          this.play('followup.notify.failed');
        }
      );
      return;
    }
    if (item.kind === 'notify') {
      if (!this.session.isLoggedIn()) {
        item.used = false;
        this.say(this.render('Sign in to PlateX first and I can set up notifications for ' + reg + '.'), { error: true });
        this.play('followup.notify.failed');
        return;
      }
      // Same call the rest of dev2 uses (Initials register, Live Auctions): the plate
      // lands in Account -> Followed plates with alerts on. (The old
      // /userdetails/notify list is not shown on that page.)
      this.account.follow(reg, 'notify').subscribe(
        () => {
          this.say(this.render('Done. **' + reg + '** is now in your **Followed plates** with alerts on. You can change the alerts there.'));
          this.play('followup.notify.done');
        },
        () => {
          item.used = false;
          this.say(this.render('Sorry, I couldn\'t follow ' + reg + ' just now. Please try again.'), { error: true });
          this.play('followup.notify.failed');
        }
      );
    }
  }

  monthYear(d: string): string {
    const t = new Date(d);
    return isNaN(t.getTime()) ? d : t.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }

  /** Plate Detail V2. On dev2 the canonical /plate route is V2; elsewhere (local dev)
   *  /plate is still the legacy page, so link the explicit V2 route instead. */
  /** In-answer jump links (#sec-...) scroll the plate page to that section instead of navigating. */
  onBubbleClick(ev: MouseEvent): void {
    const a = (ev.target as HTMLElement | null)?.closest('a');
    const href = a?.getAttribute('href') || '';
    if (!href.startsWith('#sec-')) return;
    ev.preventDefault();
    const el = document.getElementById(href.slice(1));
    if (!el) return;
    if (window.innerWidth < 768 && this.open) this.toggle(); // the panel covers the page on phones
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  plateUrl(reg: string): string {
    const r = encodeURIComponent((reg || '').replace(/\s/g, '').toUpperCase());
    const onDev2 = typeof window !== 'undefined' && window.location.hostname.includes('dev2');
    return (onDev2 ? '/plate/' : '/plate-detail-v2/') + r;
  }

  /** Same-site links (dev2) open in-app; others in a new tab. */
  openSite(url: string): void {
    const m = /^https?:\/\/[^/]*platex\.co\.uk(\/.*)$/.exec(url);
    if (m) this.router.navigateByUrl(m[1]);
    else window.open(url, '_blank', 'noopener');
  }

  openAuction(l: any): void {
    this.router.navigateByUrl('/auctions?q=' + encodeURIComponent((l.registration || '').replace(/\s/g, '')));
  }

  // ---- live auction cards: countdown ticks once a second while any are shown ----
  private tick?: any;
  private startTick(): void {
    if (this.tick) return;
    this.zone.runOutsideAngular(() => {
      this.tick = setInterval(() => {
        this.checkCountdowns();
        if (!this.open) return;
        this.zone.run(() => this.cdr.markForCheck());
      }, 1000);
    });
  }
  private secsTo(iso: string): number | null {
    const t = iso ? Date.parse(iso) : NaN;
    return isNaN(t) ? null : Math.round((t - Date.now()) / 1000);
  }
  private dur(secs: number): string {
    const d = Math.floor(secs / 86400), h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60), s = secs % 60;
    if (d) return `${d}d ${h}h`;
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
  }
  /** A DVLA lot shown in the chat crosses into its final countdown (the last 15 min,
   *  same as the red card): Plato plays the auction moment once for that lot. Lots that
   *  arrive already closing are covered by the server's auctionClosing reaction. */
  private checkCountdowns(): void {
    let fire = false;
    for (const m of this.messages) {
      for (const l of m.lots || []) {
        if (!/dvla/i.test(l.house || '')) continue;
        const key = (l.registration || '') + '|' + (l.closes_at || '');
        const closing = this.closingSoon(l);
        if (!(key in this.countdownSeen)) this.countdownSeen[key] = closing;
        else if (closing && !this.countdownSeen[key]) {
          this.countdownSeen[key] = true;
          fire = true;
        }
      }
    }
    if (fire && !this.busy) {
      this.zone.run(() => {
        this.showPlato();
        this.play('auction.final_countdown');
        this.cdr.markForCheck();
      });
    }
  }
  closingSoon(l: any): boolean {
    const s = this.secsTo(l.closes_at);
    return l.status === 'live' && s !== null && s > 0 && s <= 900;
  }
  lotPrice(l: any): string {
    if (l.result && l.result.hammer) return 'Sold ' + l.result.hammer + (l.result.hammer_is_estimated ? ' (est.)' : '');
    if (l.current_bid) return l.current_bid;
    if (l.starting_price) return 'From ' + l.starting_price;
    return 'No bids yet';
  }
  lotWhen(l: any): string {
    if (l.status === 'soon') {
      const s = this.secsTo(l.opens_at || '');
      return s && s > 0 ? 'Opens in ' + this.dur(s) : 'Opening soon';
    }
    if (l.status === 'ended') return l.result && l.result.outcome !== 'not confirmed yet' ? l.result.outcome : 'Ended · result pending';
    const s = this.secsTo(l.closes_at);
    if (s === null) return '';
    return s > 0 ? 'Closes in ' + this.dur(s) : 'Closing…';
  }

  openPlate(reg: string): void {
    this.router.navigateByUrl(this.plateUrl(reg));
  }

  private fail(reply: ChatMsg, msg: string): void {
    this.zone.run(() => {
      reply.error = true;
      this.sorry = true;
      reply.content = '';
      reply.html = this.render(msg);
      this.cdr.markForCheck();
    });
  }

  private scrollSoon(): void {
    setTimeout(() => {
      const el = this.scroller?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  /** Tiny, safe markdown: escape everything, then **bold**, links, "- " lists, paragraphs. */
  /** Registrations in Plato's text get the UK plate typeface (its 1 is a plain stroke, so it
   *  reads as I the way it does on a plate). Only text outside tags and links is touched. */
  private markRegistrations(html: string): string {
    const reg = /\b([A-Z]{2}[0-9]{2} [A-Z?]{3}|[A-Z][0-9]{1,3} [A-Z?]{3}|[A-Z?]{3} [0-9]{1,3}[A-Z]|[A-Z]{1,3} [0-9]{1,4}|[0-9]{1,4} [A-Z]{1,3}|[A-Z]{2}[0-9]{2}[A-Z]{3}|[A-Z][0-9]{1,3}[A-Z]{3}|[A-Z]{3}[0-9]{1,3}[A-Z]|[A-Z]{1,3}[0-9]{1,4}|[0-9]{1,4}[A-Z]{1,3})(?![A-Za-z0-9])/g;
    let inLink = 0;
    return html
      .split(/(<[^>]+>)/)
      .map((part) => {
        if (part.startsWith('<')) {
          if (/^<a[\s>]/i.test(part)) inLink++;
          else if (/^<\/a>/i.test(part)) inLink = Math.max(0, inLink - 1);
          return part;
        }
        return inLink ? part : part.replace(reg, '<span class="aw-reg">$1</span>');
      })
      .join('');
  }

  render(text: string, plain = false): string {
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    let s = esc(text || '');
    if (plain) return s.replace(/\n/g, '<br>');
    const link = (url: string, label: string) => {
      const m = /^https?:\/\/dev2\.platex\.co\.uk\/plate\/([A-Za-z0-9]+)/.exec(url);
      const href = m ? this.plateUrl(m[1]) : url;
      return `<a href="${href}" ${/platex\.co\.uk/.test(url) ? '' : 'target="_blank" rel="noopener"'}>${label}</a>`;
    };
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, l, u) => link(u, l));
    // [Market evidence](#sec-market): a jump to that section of the page the user is on.
    // Only when that section is on the page the user is viewing; otherwise just the words.
    s = s.replace(/\[([^\]]+)\]\(#(sec-[a-z-]+)\)/g, (_m, l, id) =>
      typeof document !== 'undefined' && document.getElementById(id) ? `<a class="aw-jump" href="#${id}">${l}</a>` : l
    );
    s = s.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, (_m, pre, u) => {
      const label = u.replace(/^https?:\/\/(dev2\.|www\.)?/, '').replace(/\/$/, '');
      return pre + link(u, label);
    });
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Plato's acted-out tricks (*does a backflip*) aren't shown as text: the animation plays them. While
    // streaming, an unfinished "*does a back" at the end is held back too.
    s = s.replace(/(^|[^*\w])\*([A-Za-z][^*\n]{1,40})\*(?![*\w])/g, '$1');
    s = s.replace(/(^|[^*\w])\*[A-Za-z][^*\n]{0,40}$/, '$1');
    s = s.replace(/[ \t]{2,}/g, ' ').replace(/^[ \t]+|[ \t]+$/gm, '').trim();
    s = this.markRegistrations(s);
    // Paragraphs and "- " lists; a list may follow an intro line in the same block ("You can buy these:\n- ...").
    const isItem = (l: string) => /^\s*[-*•] /.test(l);
    const blocks = s.split(/\n{2,}/).map((b) => {
      const out: string[] = [];
      let para: string[] = [];
      let items: string[] = [];
      const flushPara = () => { if (para.length) out.push('<p>' + para.join('<br>') + '</p>'); para = []; };
      const flushItems = () => { if (items.length) out.push('<ul>' + items.map((l) => '<li>' + l + '</li>').join('') + '</ul>'); items = []; };
      for (const l of b.split('\n')) {
        if (isItem(l)) { flushPara(); items.push(l.replace(/^\s*[-*•] /, '')); }
        else { flushItems(); para.push(l); }
      }
      flushPara();
      flushItems();
      return out.join('');
    });
    return blocks.join('');
  }
}
